import { FC, useCallback, useEffect, useState, RefObject } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { doesBannerOverlapFooter, DEFAULT_JUMBOTRON_HEIGHT } from 'modules/marketing-pages/utils';
import classNames from 'classnames';

import { openNewRequestModal } from 'modules/requests/service';
import Button from 'modules/common/components/Button';
import { JobType } from 'modules/job-types/types';

import styles from './BannerSticky.module.scss';

const DEBOUNCE_TIME = 300;

interface Props {
  jobType: JobType;
  lastContainer: RefObject<HTMLDivElement>;
}

const BannerSticky: FC<Props> = ({ lastContainer, jobType }) => {
  const [isShown, setVisibility] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleClick = useCallback(async () => {
    await openNewRequestModal({
      dispatch,
      properties: { jobTypeId: jobType.id },
    });
  }, [dispatch, jobType.id]);

  useEffect(() => {
    const subscription = fromEvent(window, 'scroll')
      .pipe(debounceTime(DEBOUNCE_TIME))
      .subscribe(() => {
        setVisibility(
          window.scrollY >= DEFAULT_JUMBOTRON_HEIGHT &&
            !doesBannerOverlapFooter({
              maxDisplayDocumentY:
                lastContainer.current &&
                lastContainer.current.offsetHeight + lastContainer.current.offsetTop,
            })
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [lastContainer]);

  // NOTE: we cannot use `use-media` nor `react-use` because of Node version,
  // so we have to do it by CSS.
  return (
    <>
      <div
        className={classNames(styles['desktop-cta-container'], {
          [styles['cta-sticky']]: isShown,
        })}
      >
        <Button color={'action'} onClick={handleClick}>
          {t('jobTypeSelect.searchProByJobType', { jobType: jobType.translations?.requestType })}
        </Button>
      </div>
      <div
        className={classNames({
          [styles['banner-container']]: true,
          [styles['hide-banner']]: !isShown,
        })}
      >
        <Button
          rounded={true}
          color={'action'}
          onClick={handleClick}
          data-test-id="request-start-sticky-btn"
        >
          {t('jobTypeSelect.searchPro')}
        </Button>
      </div>
    </>
  );
};

export default BannerSticky;
